import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Power, 
  Wifi, 
  WifiOff, 
  AlertTriangle, 
  CheckCircle, 
  Loader2,
  Moon,
  Sun,
  RefreshCw,
  Settings,
  Send,
  Users,
  XCircle,
  Link2,
  ChevronDown,
  ChevronRight,
  Download,
  Upload,
  FileUp,
  Search,
  File,
  HardDrive
} from 'lucide-react';
import DeviceConfigPanel from './DeviceConfigPanel';
import ConfigBindingPanel from './ConfigBindingPanel';

// Helper to format timestamp
const formatTwinTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// Helper to remove $metadata and $version from twin data
const cleanTwinData = (obj) => {
  if (typeof obj !== 'object' || obj === null) return obj;
  if (Array.isArray(obj)) return obj;
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === '$metadata' || key === '$version') continue;
    cleaned[key] = cleanTwinData(value);
  }
  return cleaned;
};

// Component to display a single property section
const TwinPropertySection = ({ title, data, metadata, color = 'blue' }) => {
  const [expanded, setExpanded] = useState(true);
  const cleanedData = cleanTwinData(data);
  const lastUpdated = metadata?.$lastUpdated;
  
  const colorClasses = {
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    purple: 'border-purple-200 bg-purple-50',
    orange: 'border-orange-200 bg-orange-50',
    gray: 'border-gray-200 bg-gray-50'
  };
  
  const headerColors = {
    blue: 'text-blue-700',
    green: 'text-green-700',
    purple: 'text-purple-700',
    orange: 'text-orange-700',
    gray: 'text-gray-700'
  };

  return (
    <div className={`border rounded-lg ${colorClasses[color]} overflow-hidden`}>
      <div 
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className={`font-semibold ${headerColors[color]}`}>{title}</span>
        </div>
        {lastUpdated && (
          <span className="text-xs text-gray-500">
            {formatTwinTimestamp(lastUpdated)}
          </span>
        )}
      </div>
      {expanded && (
        <div className="p-3 pt-0 space-y-2">
          {Object.entries(cleanedData).map(([key, value]) => (
            <div key={key} className="bg-white rounded p-2 border border-white/50">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm text-gray-700">{key}</span>
                {metadata?.[key]?.$lastUpdated && (
                  <span className="text-xs text-gray-400">
                    {formatTwinTimestamp(metadata[key].$lastUpdated)}
                  </span>
                )}
              </div>
              {typeof value === 'object' && value !== null ? (
                <div className="mt-1 space-y-1">
                  {Object.entries(value).map(([subKey, subValue]) => (
                    <div key={subKey} className="flex justify-between text-sm pl-3 border-l-2 border-gray-200">
                      <span className="text-gray-600">{subKey}</span>
                      <span className="font-mono text-gray-800">
                        {Array.isArray(subValue) ? JSON.stringify(subValue) : String(subValue)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="font-mono text-sm text-gray-800 mt-1">
                  {Array.isArray(value) ? JSON.stringify(value) : String(value)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Component for batch twin result item (expandable)
const BatchTwinResultItem = ({ result }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className={`border rounded-lg overflow-hidden ${result.success ? 'border-green-200' : 'border-red-200'}`}>
      <div 
        className={`flex items-center justify-between p-3 cursor-pointer ${
          result.success ? 'bg-green-50 hover:bg-green-100' : 'bg-red-50 hover:bg-red-100'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-mono font-medium">{result.serial_number}</span>
        </div>
        <div className="flex items-center gap-2">
          {result.success ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <XCircle className="w-4 h-4 text-red-500" />
          )}
        </div>
      </div>
      {expanded && (
        <div className="p-3 bg-white border-t">
          {result.success && result.data ? (
            <DeviceTwinDisplay data={result.data} />
          ) : (
            <p className="text-sm text-red-600">{result.error || 'Unknown error'}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Main Device Twin Display Component
const DeviceTwinDisplay = ({ data }) => {
  const [activeTab, setActiveTab] = useState('desired');
  const [showRaw, setShowRaw] = useState(false);
  
  const desired = data?.desired || {};
  const reported = data?.reported || {};
  
  const desiredClean = cleanTwinData(desired);
  const reportedClean = cleanTwinData(reported);
  
  const desiredMetadata = desired.$metadata || {};
  const reportedMetadata = reported.$metadata || {};
  
  const sectionColors = ['blue', 'green', 'purple', 'orange', 'gray'];
  
  const renderSections = (twinData, metadata) => {
    const sections = Object.keys(twinData);
    return sections.map((section, idx) => (
      <TwinPropertySection
        key={section}
        title={section}
        data={twinData[section]}
        metadata={metadata[section]}
        color={sectionColors[idx % sectionColors.length]}
      />
    ));
  };

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveTab('desired')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
            activeTab === 'desired' 
              ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500' 
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          Desired Properties
          <Badge variant="outline" className="ml-2 text-xs">
            v{desired.$version || '?'}
          </Badge>
        </button>
        <button
          onClick={() => setActiveTab('reported')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
            activeTab === 'reported' 
              ? 'bg-green-100 text-green-700 border-b-2 border-green-500' 
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          Reported Properties
          <Badge variant="outline" className="ml-2 text-xs">
            v{reported.$version || '?'}
          </Badge>
        </button>
        <button
          onClick={() => setShowRaw(!showRaw)}
          className={`ml-auto px-3 py-1 rounded text-xs font-medium transition-colors ${
            showRaw 
              ? 'bg-gray-200 text-gray-700' 
              : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          {showRaw ? 'Hide Raw' : 'Show Raw'}
        </button>
      </div>
      
      {/* Content */}
      {showRaw ? (
        <pre className="bg-white p-3 rounded border text-xs overflow-x-auto max-h-96">
          {JSON.stringify(activeTab === 'desired' ? desired : reported, null, 2)}
        </pre>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
          {activeTab === 'desired' ? (
            Object.keys(desiredClean).length > 0 ? (
              renderSections(desiredClean, desiredMetadata)
            ) : (
              <p className="text-gray-500 text-center py-4">No desired properties configured</p>
            )
          ) : (
            Object.keys(reportedClean).length > 0 ? (
              renderSections(reportedClean, reportedMetadata)
            ) : (
              <p className="text-gray-500 text-center py-4">No reported properties</p>
            )
          )}
        </div>
      )}
      
      {/* Last Updated Info */}
      <div className="text-xs text-gray-400 text-right border-t pt-2">
        {activeTab === 'desired' && desiredMetadata.$lastUpdated && (
          <span>Last updated: {formatTwinTimestamp(desiredMetadata.$lastUpdated)}</span>
        )}
        {activeTab === 'reported' && reportedMetadata.$lastUpdated && (
          <span>Last updated: {formatTwinTimestamp(reportedMetadata.$lastUpdated)}</span>
        )}
      </div>
    </div>
  );
};

function OnboardingManager({ state, setState }) {
  // Local loading states (don't need to persist)
  const [loading, setLoading] = useState(false);
  const [wakeupLoading, setWakeupLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [bindingLoading, setBindingLoading] = useState(false);
  const [getTwinLoading, setGetTwinLoading] = useState(false);
  const [configuredDevices, setConfiguredDevices] = useState([]);
  
  // Global device serial (shared across all cards)
  const [globalDeviceSerial, setGlobalDeviceSerial] = useState('');
  
  // Device Info state (local)
  const [deviceInfoLoading, setDeviceInfoLoading] = useState(false);
  const [deviceInfoResult, setDeviceInfoResult] = useState(null);
  const [deviceInfoError, setDeviceInfoError] = useState(null);

  // Card collapse states
  const [remoteWakeupExpanded, setRemoteWakeupExpanded] = useState(true);
  const [deviceInfoExpanded, setDeviceInfoExpanded] = useState(true);
  const [configBindingExpanded, setConfigBindingExpanded] = useState(true);
  const [mdvrConfigExpanded, setMdvrConfigExpanded] = useState(true);
  const [upgradeFilesExpanded, setUpgradeFilesExpanded] = useState(true);
  const [customConfigExpanded, setCustomConfigExpanded] = useState(true);

  // Upgrade files state
  const [upgradeFilesLoading, setUpgradeFilesLoading] = useState(false);
  const [upgradeFilesResult, setUpgradeFilesResult] = useState(null);
  const [upgradeFilesError, setUpgradeFilesError] = useState(null);
  const [upgradeFileType, setUpgradeFileType] = useState('UPGRADE');
  const [upgradeFileNameFilter, setUpgradeFileNameFilter] = useState('');

  // Maintenance task state
  const [selectedUpgradeFile, setSelectedUpgradeFile] = useState(null);
  const [upgradeDeviceIds, setUpgradeDeviceIds] = useState('');
  const [maintenanceTaskType, setMaintenanceTaskType] = useState('REMOTE_UPGRADE');
  const [maintenanceTaskLoading, setMaintenanceTaskLoading] = useState(false);
  const [maintenanceTaskResult, setMaintenanceTaskResult] = useState(null);
  const [maintenanceTaskError, setMaintenanceTaskError] = useState(null);

  // Custom config task state
  const [customConfigDeviceIds, setCustomConfigDeviceIds] = useState('');
  const [customConfigParam, setCustomConfigParam] = useState('');
  const [customConfigLoading, setCustomConfigLoading] = useState(false);
  const [customConfigResult, setCustomConfigResult] = useState(null);
  const [customConfigError, setCustomConfigError] = useState(null);

  // Task status state
  const [taskStatusIds, setTaskStatusIds] = useState('');
  const [taskStatusLoading, setTaskStatusLoading] = useState(false);
  const [taskStatusResult, setTaskStatusResult] = useState(null);
  const [taskStatusError, setTaskStatusError] = useState(null);

  // Helper to update a single field in state (supports callback functions)
  const updateState = (field, value) => {
    setState(prev => ({
      ...prev,
      [field]: typeof value === 'function' ? value(prev[field]) : value
    }));
  };

  // Destructure state for easier access
  const {
    deviceId, deviceStatus, error, success,
    configError, configSuccess, configResult,
    useBatchMode, selectedBatchDevices, batchResults, currentConfig,
    bindingError, bindingSuccess,
    bindingResult, bindingConfig, useBindingBatchMode, selectedBindingDevices,
    bindingBatchResults, bindingResultExpanded, bindingBatchResultsExpanded
  } = state;

  // Setters using updateState
  const setDeviceStatus = (v) => updateState('deviceStatus', v);
  const setError = (v) => updateState('error', v);
  const setSuccess = (v) => updateState('success', v);
  const setConfigError = (v) => updateState('configError', v);
  const setConfigSuccess = (v) => updateState('configSuccess', v);
  const setConfigResult = (v) => updateState('configResult', v);
  const setUseBatchMode = (v) => updateState('useBatchMode', v);
  const setSelectedBatchDevices = (v) => updateState('selectedBatchDevices', v);
  const setBatchResults = (v) => updateState('batchResults', v);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setCurrentConfig = useCallback((v) => updateState('currentConfig', v), [setState]);
  const setBindingError = (v) => updateState('bindingError', v);
  const setBindingSuccess = (v) => updateState('bindingSuccess', v);
  const setBindingResult = (v) => updateState('bindingResult', v);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setBindingConfig = useCallback((v) => updateState('bindingConfig', v), [setState]);
  const setUseBindingBatchMode = (v) => updateState('useBindingBatchMode', v);
  const setSelectedBindingDevices = (v) => updateState('selectedBindingDevices', v);
  const setBindingBatchResults = (v) => updateState('bindingBatchResults', v);
  const setBindingResultExpanded = (v) => updateState('bindingResultExpanded', v);
  const setBindingBatchResultsExpanded = (v) => updateState('bindingBatchResultsExpanded', v);

  // Load configured devices
  useEffect(() => {
    loadConfiguredDevices();
  }, []);

  const loadConfiguredDevices = async () => {
    try {
      const response = await axios.get('/api/devices');
      setConfiguredDevices(response.data.devices || []);
    } catch (err) {
      console.error('Error loading devices:', err);
    }
  };

  const handleBatchDeviceToggle = (deviceSerial) => {
    setSelectedBatchDevices(prev =>
      prev.includes(deviceSerial)
        ? prev.filter(d => d !== deviceSerial)
        : [...prev, deviceSerial]
    );
  };

  const handleSelectAllBatchDevices = () => {
    if (selectedBatchDevices.length === configuredDevices.length) {
      setSelectedBatchDevices([]);
    } else {
      setSelectedBatchDevices(configuredDevices.map(d => d.serial));
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const clearConfigMessages = () => {
    setConfigError(null);
    setConfigSuccess(null);
  };

  const handleGetStatus = async () => {
    if (!globalDeviceSerial.trim()) {
      setError('Please enter a device serial number');
      return;
    }

    clearMessages();
    setLoading(true);
    setDeviceStatus(null);

    try {
      const response = await axios.post('/api/ftcloud/device/status', {
        device_id: globalDeviceSerial.trim()
      });
      
      setDeviceStatus(response.data);
    } catch (err) {
      const errorData = err.response?.data;
      
      if (err.response?.status === 404) {
        setError(`Device ${globalDeviceSerial} not found in FTCloud.`);
      } else {
        setError(errorData?.error || 'Failed to get device status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWakeup = async () => {
    if (!globalDeviceSerial.trim()) {
      setError('Please enter a device serial number');
      return;
    }

    clearMessages();
    setWakeupLoading(true);

    try {
      const response = await axios.post('/api/ftcloud/device/wakeup', {
        device_id: globalDeviceSerial.trim()
      });
      
      if (response.data.success) {
        setSuccess(`Wakeup command sent successfully to ${globalDeviceSerial}`);
        // Refresh status after wakeup
        setTimeout(() => handleGetStatus(), 2000);
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      if (errorData?.error_code === '201054') {
        setError('Device is not in dormant state. Wakeup not needed.');
      } else if (errorData?.error_code === '201057') {
        setError('Failed to wake up device. Please try again.');
      } else if (err.response?.status === 404) {
        setError(`Device ${globalDeviceSerial} not found in FTCloud.`);
      } else {
        setError(errorData?.error || 'Failed to wake up device');
      }
    } finally {
      setWakeupLoading(false);
    }
  };

  const handleGetDeviceInfo = async () => {
    if (!globalDeviceSerial.trim()) {
      setDeviceInfoError('Please enter a device serial number');
      return;
    }

    setDeviceInfoLoading(true);
    setDeviceInfoError(null);
    setDeviceInfoResult(null);

    try {
      const response = await axios.post('/api/ftcloud/device/info', {
        serial_number: globalDeviceSerial.trim()
      });
      
      if (response.data.success) {
        setDeviceInfoResult(response.data);
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      if (err.response?.status === 404) {
        setDeviceInfoError(`Device ${globalDeviceSerial} not found.`);
      } else {
        setDeviceInfoError(errorData?.error || 'Failed to get device info');
      }
    } finally {
      setDeviceInfoLoading(false);
    }
  };

  const handleUpdateConfig = async () => {
    // Validate based on mode
    if (useBatchMode) {
      if (selectedBatchDevices.length === 0) {
        setConfigError('Please select at least one device');
        return;
      }
    } else {
      if (!globalDeviceSerial.trim()) {
        setConfigError('Please enter a device serial number');
        return;
      }
    }

    if (!currentConfig || typeof currentConfig !== 'object') {
      setConfigError('Invalid configuration payload. Please verify the JSON.');
      return;
    }
    
    clearConfigMessages();
    setConfigLoading(true);
    setConfigResult(null);
    setBatchResults(null);

    try {
      if (useBatchMode) {
        // Batch mode - send to multiple devices
        const response = await axios.post('/api/ftcloud/device/mdvr-config/batch', {
          device_ids: selectedBatchDevices,
          data: currentConfig
        });
        
        setBatchResults(response.data);
        if (response.data.success) {
          setConfigSuccess(`Configuration sent to all ${response.data.total} devices successfully`);
        } else {
          setConfigSuccess(`Configuration sent: ${response.data.success_count} succeeded, ${response.data.failed_count} failed`);
        }
      } else {
        // Single device mode
        const response = await axios.post('/api/ftcloud/device/mdvr-config', {
          unique_id: globalDeviceSerial.trim(),
          data: currentConfig
        });
        
        if (response.data.success) {
          setConfigSuccess('MDVR configuration updated successfully');
          setConfigResult(response.data);
        }
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      if (errorData?.error_type === 'config_error') {
        setConfigError(errorData?.error || 'Configuration error. Check credentials.');
      } else if (err.response?.status === 404) {
        setConfigError(`Device ${globalDeviceSerial} not found.`);
      } else {
        setConfigError(errorData?.error || 'Failed to update MDVR configuration');
      }
    } finally {
      setConfigLoading(false);
    }
  };

  // Config Binding handlers
  const clearBindingMessages = () => {
    setBindingError(null);
    setBindingSuccess(null);
  };

  const handleGetDeviceTwin = async () => {
    if (!globalDeviceSerial.trim()) {
      setBindingError('Please enter a device serial number');
      return;
    }
    
    clearBindingMessages();
    setGetTwinLoading(true);
    setBindingResult(null);
    
    try {
      const response = await axios.get('/api/device/config-binding', {
        params: {
          serial_number: globalDeviceSerial
        }
      });
      
      if (response.data.success) {
        setBindingSuccess('Device twin retrieved successfully');
        setBindingResult(response.data);
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (err.response?.status === 404) {
        setBindingError(`Device ${globalDeviceSerial} not found.`);
      } else {
        setBindingError(errorData?.error || 'Failed to get device twin');
      }
    } finally {
      setGetTwinLoading(false);
    }
  };

  const handleGetDeviceTwinBatch = async () => {
    if (selectedBindingDevices.length === 0) {
      setBindingError('Please select at least one device');
      return;
    }
    
    clearBindingMessages();
    setGetTwinLoading(true);
    setBindingResult(null);
    setBindingBatchResults(null);
    
    const results = [];
    let successCount = 0;
    let failedCount = 0;
    
    for (const serial of selectedBindingDevices) {
      const device = configuredDevices.find(d => d.serial === serial);
      if (!device) continue;
      
      try {
        const response = await axios.get('/api/device/config-binding', {
          params: {
            serial_number: serial
          }
        });

        if (response.data.success) {
          successCount++;
          results.push({
            serial_number: serial,
            success: true,
            data: response.data.data
          });
        } else {
          failedCount++;
          results.push({
            serial_number: serial,
            success: false,
            error: response.data.error || 'Unknown error'
          });
        }
      } catch (err) {
        failedCount++;
        const errorData = err.response?.data;
        results.push({
          serial_number: serial,
          success: false,
          error: errorData?.error || 'Failed to get device twin'
        });
      }
    }
    
    setBindingBatchResults({
      success: failedCount === 0,
      total: results.length,
      success_count: successCount,
      failed_count: failedCount,
      results: results,
      isGetTwin: true
    });
    
    if (failedCount === 0) {
      setBindingSuccess(`Device twin retrieved for all ${successCount} devices`);
    } else {
      setBindingSuccess(`Device twin retrieved: ${successCount} succeeded, ${failedCount} failed`);
    }
    
    setGetTwinLoading(false);
  };

  const handleBindingDeviceToggle = (deviceSerial) => {
    setSelectedBindingDevices(prev =>
      prev.includes(deviceSerial)
        ? prev.filter(d => d !== deviceSerial)
        : [...prev, deviceSerial]
    );
  };

  const handleSelectAllBindingDevices = () => {
    if (selectedBindingDevices.length === configuredDevices.length) {
      setSelectedBindingDevices([]);
    } else {
      setSelectedBindingDevices(configuredDevices.map(d => d.serial));
    }
  };

  const handleUpdateConfigBinding = async () => {
    // Validate based on mode
    if (useBindingBatchMode) {
      if (selectedBindingDevices.length === 0) {
        setBindingError('Please select at least one device');
        return;
      }
    } else {
      if (!globalDeviceSerial.trim()) {
        setBindingError('Please enter a device serial number');
        return;
      }
    }

    // Check if any config is selected
    if (Object.keys(bindingConfig).length === 0) {
      setBindingError('Please select at least one configuration to send');
      return;
    }
    
    clearBindingMessages();
    setBindingLoading(true);
    setBindingResult(null);
    setBindingBatchResults(null);

    try {
      if (useBindingBatchMode) {
        // Batch mode - send to multiple devices
        const devices = selectedBindingDevices.map(serial => ({
          serial_number: serial
        }));

        const response = await axios.put('/api/device/config-binding/batch', {
          devices: devices,
          config: bindingConfig
        });
        
        setBindingBatchResults(response.data);
        if (response.data.success) {
          setBindingSuccess(`Config binding sent to all ${response.data.total} devices successfully`);
        } else {
          setBindingSuccess(`Config binding sent: ${response.data.success_count} succeeded, ${response.data.failed_count} failed`);
        }
      } else {
        // Single device mode
        const response = await axios.put('/api/device/config-binding', {
          serial_number: globalDeviceSerial.trim(),
          config: bindingConfig
        });
        
        if (response.data.success) {
          setBindingSuccess('Config binding updated successfully');
          setBindingResult(response.data);
        }
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      if (err.response?.status === 404) {
        setBindingError(`Device ${globalDeviceSerial} not found.`);
      } else {
        setBindingError(errorData?.error || 'Failed to update config binding');
      }
    } finally {
      setBindingLoading(false);
    }
  };

  const handleGetUpgradeFiles = async () => {
    setUpgradeFilesLoading(true);
    setUpgradeFilesError(null);
    setUpgradeFilesResult(null);

    try {
      const params = new URLSearchParams({
        file_type: upgradeFileType,
        page: '1',
        page_size: '100'
      });
      
      if (upgradeFileNameFilter.trim()) {
        params.append('file_name', upgradeFileNameFilter.trim());
      }

      const response = await axios.get(`/api/ftcloud/upgrade-files?${params}`);
      setUpgradeFilesResult(response.data);
    } catch (err) {
      const errorData = err.response?.data;
      
      setUpgradeFilesError(errorData?.error || 'Failed to fetch upgrade files');
    } finally {
      setUpgradeFilesLoading(false);
    }
  };

  const handleCreateMaintenanceTask = async () => {
    if (!selectedUpgradeFile && (maintenanceTaskType === 'REMOTE_UPGRADE' || maintenanceTaskType === 'PROFILE_ISSUED')) {
      setMaintenanceTaskError('Please select an upgrade file first');
      return;
    }
    
    if (!upgradeDeviceIds.trim()) {
      setMaintenanceTaskError('Please enter at least one device serial number');
      return;
    }

    setMaintenanceTaskLoading(true);
    setMaintenanceTaskError(null);
    setMaintenanceTaskResult(null);

    try {
      const deviceIds = upgradeDeviceIds.split(',').map(id => id.trim()).filter(id => id);
      
      const response = await axios.post('/api/ftcloud/maintenance-task', {
        unique_ids: deviceIds,
        file_id: selectedUpgradeFile?.fileId || selectedUpgradeFile?.id,
        task_type: maintenanceTaskType
      });
      
      setMaintenanceTaskResult(response.data);
    } catch (err) {
      const errorData = err.response?.data;
      setMaintenanceTaskError(errorData?.error || 'Failed to create maintenance task');
    } finally {
      setMaintenanceTaskLoading(false);
    }
  };

  const handleCreateCustomConfigTask = async () => {
    if (!customConfigParam.trim()) {
      setCustomConfigError('Please enter custom configuration parameter');
      return;
    }
    
    if (!customConfigDeviceIds.trim()) {
      setCustomConfigError('Please enter at least one device ID');
      return;
    }

    setCustomConfigLoading(true);
    setCustomConfigError(null);
    setCustomConfigResult(null);

    try {
      const deviceIds = customConfigDeviceIds.split(',').map(id => id.trim()).filter(id => id);
      
      const response = await axios.post('/api/ftcloud/maintenance-task', {
        unique_ids: deviceIds,
        task_type: 'CUSTOM_PARAM_ISSUED',
        custom_param: customConfigParam
      });
      
      setCustomConfigResult(response.data);
    } catch (err) {
      const errorData = err.response?.data;
      
      setCustomConfigError(errorData?.error || 'Failed to create custom config task');
    } finally {
      setCustomConfigLoading(false);
    }
  };

  const handleGetTaskStatus = async () => {
    if (!taskStatusIds.trim()) {
      setTaskStatusError('Please enter at least one task ID');
      return;
    }

    setTaskStatusLoading(true);
    setTaskStatusError(null);
    setTaskStatusResult(null);

    try {
      const taskIds = taskStatusIds.split(',').map(id => id.trim()).filter(id => id);
      
      const response = await axios.post('/api/ftcloud/maintenance-task/status', {
        task_ids: taskIds
      });
      
      setTaskStatusResult(response.data);
    } catch (err) {
      const errorData = err.response?.data;
      
      setTaskStatusError(errorData?.error || 'Failed to get task status');
    } finally {
      setTaskStatusLoading(false);
    }
  };

  const renderStatusBadge = (status) => {
    if (!status) return null;
    
    const data = status.data || {};
    const dormantState = data.dormantState?.toUpperCase?.() || data.dormantState;
    const onlineState = data.onlineState?.toUpperCase?.() || data.onlineState;
    
    const isDormant = dormantState === 'DORMANT' || data.dormant === true;
    const isAwake = dormantState === 'AWAKE';
    const isOnline = onlineState === 'ONLINE' || data.online === true;
    const isOffline = onlineState === 'OFFLINE';
    
    if (isDormant) {
      return (
        <Badge variant="secondary" className="bg-purple-100 text-purple-700 flex items-center gap-1">
          <Moon className="w-3 h-3" />
          Dormant
        </Badge>
      );
    } else if (isAwake && isOnline) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-700 flex items-center gap-1">
          <Sun className="w-3 h-3" />
          Online
        </Badge>
      );
    } else if (isAwake && isOffline) {
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-700 flex items-center gap-1">
          <WifiOff className="w-3 h-3" />
          Awake (Offline)
        </Badge>
      );
    } else if (isOnline) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-700 flex items-center gap-1">
          <Sun className="w-3 h-3" />
          Online
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Global Device Serial Input */}
      <Card className="border-2 border-primary/30 bg-gradient-to-r from-blue-50 to-cyan-50">
        <CardContent className="py-4 space-y-2">
          <div className="flex gap-4 items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="globalDeviceSerial" className="text-base font-semibold">Device Serial Number</Label>
              <Input
                id="globalDeviceSerial"
                placeholder="Enter device serial (e.g., 00D2000F93)"
                value={globalDeviceSerial}
                onChange={(e) => setGlobalDeviceSerial(e.target.value)}
                className="text-lg h-11"
              />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            These settings will be used for all operations below.
          </p>
        </CardContent>
      </Card>

      {/* Remote Wakeup Card */}
      <Card className="border-2">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setRemoteWakeupExpanded(!remoteWakeupExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Power className="w-6 h-6 text-blue-600" />
              Remote Wakeup
            </div>
            {remoteWakeupExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </CardTitle>
          {remoteWakeupExpanded && (
            <CardDescription>
              Wake up devices from dormant state via FTCloud API.
            </CardDescription>
          )}
        </CardHeader>
        {remoteWakeupExpanded && (
        <CardContent className="space-y-4">
          {/* Action Buttons */}
          <div className="flex gap-4 items-center">
            <Button
              onClick={handleGetStatus}
              disabled={loading || !globalDeviceSerial.trim()}
              variant="outline"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="ml-2">Check Status</span>
            </Button>
            <span className="text-sm text-muted-foreground">
              Using: <span className="font-mono font-medium">{globalDeviceSerial || '(no device)'}</span>
            </span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Error</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-green-700">Success</p>
                <p className="text-sm text-green-600">{success}</p>
              </div>
            </div>
          )}

          {/* Device Status Display */}
          {deviceStatus && (
            <Card className="bg-gray-50">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wifi className="w-5 h-5 text-blue-500" />
                    Device Status
                  </CardTitle>
                  {renderStatusBadge(deviceStatus)}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Device ID:</span>
                    <span className="ml-2 font-mono font-medium">{deviceStatus.device_id}</span>
                  </div>
                  {deviceStatus.request_id && (
                    <div>
                      <span className="text-muted-foreground">Request ID:</span>
                      <span className="ml-2 font-mono text-xs">{deviceStatus.request_id}</span>
                    </div>
                  )}
                </div>

                {/* Raw Data Display */}
                {deviceStatus.data && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-2">Device Data:</p>
                    <pre className="bg-white p-3 rounded border text-xs overflow-x-auto">
                      {JSON.stringify(deviceStatus.data, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Wakeup Button */}
                <div className="pt-4 border-t">
                  <Button
                    onClick={handleWakeup}
                    disabled={wakeupLoading}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    {wakeupLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Sending Wakeup Command...
                      </>
                    ) : (
                      <>
                        <Power className="w-4 h-4 mr-2" />
                        Wake Up Device
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Note: Device must be in dormant state with Remote Wake enabled
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Wakeup (without checking status first) */}
          {!deviceStatus && deviceId.trim() && (
            <div className="pt-4 border-t">
              <Button
                onClick={handleWakeup}
                disabled={wakeupLoading || !deviceId.trim()}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                {wakeupLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sending Wakeup Command...
                  </>
                ) : (
                  <>
                    <Power className="w-4 h-4 mr-2" />
                    Wake Up Device
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Device Info Card */}
      <Card className="border-2">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setDeviceInfoExpanded(!deviceInfoExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6 text-cyan-600" />
              Device Info
            </div>
            {deviceInfoExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </CardTitle>
          {deviceInfoExpanded && (
            <CardDescription>
              Get device information from FTCloud MDVR API.
              <span className="block mt-1 text-cyan-600 font-medium">
                Retrieves detailed device configuration and status.
              </span>
            </CardDescription>
          )}
        </CardHeader>
        {deviceInfoExpanded && (
        <CardContent className="space-y-4">
          {/* Action Buttons */}
          <div className="flex gap-4 items-center">
            <Button
              onClick={handleGetDeviceInfo}
              disabled={deviceInfoLoading || !globalDeviceSerial.trim()}
              variant="outline"
            >
              {deviceInfoLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="ml-2">Get Info</span>
            </Button>
            <span className="text-sm text-muted-foreground">
              Using: <span className="font-mono font-medium">{globalDeviceSerial || '(no device)'}</span>
            </span>
          </div>

          {/* Error Message */}
          {deviceInfoError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Error</p>
                <p className="text-sm text-red-600">{deviceInfoError}</p>
              </div>
            </div>
          )}

          {/* Device Info Result Display */}
          {deviceInfoResult && deviceInfoResult.data && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                  <p className="text-xs text-cyan-600 font-medium">Unique ID</p>
                  <p className="font-mono font-bold text-cyan-800">{deviceInfoResult.data.uniqueId}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium">Device Model</p>
                  <p className="font-medium text-blue-800 text-sm">{deviceInfoResult.data.deviceModel || '-'}</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-600 font-medium">Position Module</p>
                  <p className="font-medium text-purple-800">{deviceInfoResult.data.positionModule || '-'}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600 font-medium">Last Update</p>
                  <p className="font-medium text-gray-800 text-xs">
                    {deviceInfoResult.data.updateTime 
                      ? new Date(deviceInfoResult.data.updateTime).toLocaleString() 
                      : '-'}
                  </p>
                </div>
              </div>

              {/* Communication Info */}
              {deviceInfoResult.data.communication && deviceInfoResult.data.communication.length > 0 && (
                <Card className="border-green-200">
                  <CardHeader className="pb-2 bg-green-50">
                    <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                      <Wifi className="w-4 h-4" />
                      Communication Modules ({deviceInfoResult.data.communication.filter(c => c.imei).length} active)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-3">
                    <div className="space-y-3">
                      {deviceInfoResult.data.communication.map((comm, idx) => {
                        const isActive = comm.imei || comm.networkType;
                        return (
                          <div key={idx} className={`rounded-lg p-3 ${isActive ? 'bg-white border' : 'bg-gray-100 border border-dashed'}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-xs">
                                Module #{comm.moduleNo}
                              </Badge>
                              {isActive ? (
                                <Badge className="bg-green-500 text-xs">{comm.networkType} ({comm.networkMode})</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">Not Connected</Badge>
                              )}
                            </div>
                            {isActive && (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <div>
                                  <span className="text-muted-foreground text-xs">IMEI:</span>
                                  <span className="ml-2 font-mono text-xs">{comm.imei || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground text-xs">ICCID:</span>
                                  <span className="ml-2 font-mono text-xs">{comm.iccid || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground text-xs">IMSI:</span>
                                  <span className="ml-2 font-mono text-xs">{comm.imsi || '-'}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Storage Info */}
              {deviceInfoResult.data.storage && deviceInfoResult.data.storage.length > 0 && (
                <Card className="border-orange-200">
                  <CardHeader className="pb-2 bg-orange-50">
                    <CardTitle className="text-sm flex items-center gap-2 text-orange-700">
                      <Settings className="w-4 h-4" />
                      Storage ({deviceInfoResult.data.storage.length} units)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-3">
                    <div className="space-y-3">
                      {deviceInfoResult.data.storage.map((storage, idx) => {
                        const usedSize = storage.totalSize - storage.freeSize;
                        const usedPercent = storage.totalSize > 0 ? (usedSize / storage.totalSize) * 100 : 0;
                        return (
                          <div key={idx} className="bg-white border rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {storage.storageType} #{storage.storageNo}
                                </Badge>
                                <span className="text-xs text-muted-foreground">{storage.useType}</span>
                              </div>
                              <Badge className={
                                storage.healthStatus === 'NORMAL' ? 'bg-green-500' : 
                                storage.healthStatus === 'UNSUPPORTED' ? 'bg-gray-500' : 'bg-red-500'
                              }>
                                {storage.healthStatus}
                              </Badge>
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span>Used: {usedSize.toLocaleString()} MB</span>
                                <span>Free: {storage.freeSize.toLocaleString()} MB</span>
                                <span>Total: {storage.totalSize.toLocaleString()} MB</span>
                              </div>
                              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full transition-all ${usedPercent > 90 ? 'bg-red-500' : usedPercent > 70 ? 'bg-orange-500' : 'bg-green-500'}`}
                                  style={{ width: `${usedPercent}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Version Info */}
              {deviceInfoResult.data.version && deviceInfoResult.data.version.length > 0 && (
                <Card className="border-blue-200">
                  <CardHeader className="pb-2 bg-blue-50">
                    <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                      <Download className="w-4 h-4" />
                      Firmware Versions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-3">
                    <div className="space-y-2">
                      {deviceInfoResult.data.version.map((ver, idx) => {
                        const isMDVR = ver.moduleName === 'MDVR';
                        return (
                          <div key={idx} className={`bg-white border rounded p-2 ${isMDVR ? 'border-blue-300 bg-blue-50' : ''}`}>
                            <div className="flex items-center justify-between">
                              <span className={`text-xs font-medium ${isMDVR ? 'text-blue-700' : 'text-gray-600'}`}>
                                {ver.moduleName}
                              </span>
                              {!isMDVR && (
                                <span className="font-mono text-xs text-gray-800 truncate max-w-[250px]" title={ver.moduleVersion}>
                                  {ver.moduleVersion || '-'}
                                </span>
                              )}
                            </div>
                            {isMDVR && (
                              <p className="font-mono text-sm text-blue-800 mt-1 break-all">
                                {ver.moduleVersion || '-'}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Config Binding Card */}
      <Card className="border-2">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setConfigBindingExpanded(!configBindingExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link2 className="w-6 h-6 text-purple-600" />
              Config Binding (Device Twin)
            </div>
            {configBindingExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </CardTitle>
          {configBindingExpanded && (
            <CardDescription>
              Update device twin desired properties via Azure IoT Hub API.
              <span className="block mt-1 text-blue-600 font-medium">
                Configures device behavior through cloud-to-device twin updates.
              </span>
            </CardDescription>
          )}
        </CardHeader>
        {configBindingExpanded && (
        <CardContent className="space-y-4">
          {/* Mode Toggle */}
          <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="bindingBatchMode"
              checked={useBindingBatchMode}
              onChange={(e) => setUseBindingBatchMode(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            <Label htmlFor="bindingBatchMode" className="flex items-center gap-2 cursor-pointer">
              <Users className="w-4 h-4" />
              Send to multiple devices (batch mode)
            </Label>
          </div>

          {/* Single Device Info */}
          {!useBindingBatchMode && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Using: <span className="font-mono font-medium">{globalDeviceSerial || '(no device)'}</span>
              </span>
            </div>
          )}

          {/* Batch Device Selection */}
          {useBindingBatchMode && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Select Devices ({selectedBindingDevices.length} selected)</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAllBindingDevices}
                  disabled={configuredDevices.length === 0}
                >
                  {selectedBindingDevices.length === configuredDevices.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                {configuredDevices.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No devices configured. Add devices in the Devices tab.
                  </p>
                ) : (
                  configuredDevices.map((device) => (
                    <div
                      key={device.full}
                      className={`flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer ${
                        selectedBindingDevices.includes(device.serial) ? 'bg-purple-50' : ''
                      }`}
                      onClick={() => handleBindingDeviceToggle(device.serial)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedBindingDevices.includes(device.serial)}
                        onChange={() => handleBindingDeviceToggle(device.serial)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="font-mono text-sm">{device.serial}</span>
                    </div>
                  ))
                )}
              </div>

              {/* Get Current Config for selected devices in batch mode */}
              <Button
                variant="outline"
                onClick={handleGetDeviceTwinBatch}
                disabled={getTwinLoading || selectedBindingDevices.length === 0}
                className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                {getTwinLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Getting Device Twin ({selectedBindingDevices.length} devices)...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Get Current Config ({selectedBindingDevices.length} {selectedBindingDevices.length === 1 ? 'device' : 'devices'})
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Config Binding Panel */}
          <ConfigBindingPanel onConfigGenerated={setBindingConfig} />

          {/* Get Current Config Button (Single Device Mode) */}
          {!useBindingBatchMode && (
            <Button
              variant="outline"
              onClick={handleGetDeviceTwin}
              disabled={getTwinLoading || !globalDeviceSerial.trim()}
              className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              {getTwinLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Getting Device Twin...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Get Current Config (Device Twin)
                </>
              )}
            </Button>
          )}

          {/* Send Button */}
          <Button
            onClick={handleUpdateConfigBinding}
            disabled={bindingLoading || (useBindingBatchMode ? selectedBindingDevices.length === 0 : !globalDeviceSerial.trim()) || Object.keys(bindingConfig).length === 0}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {bindingLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {useBindingBatchMode ? `Sending to ${selectedBindingDevices.length} devices...` : 'Updating Config Binding...'}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {useBindingBatchMode ? `Send to ${selectedBindingDevices.length} Devices` : 'Update Config Binding'}
              </>
            )}
          </Button>

          {/* Binding Error Message */}
          {bindingError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Error</p>
                <p className="text-sm text-red-600">{bindingError}</p>
              </div>
            </div>
          )}

          {/* Binding Success Message */}
          {bindingSuccess && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-green-700">Success</p>
                <p className="text-sm text-green-600">{bindingSuccess}</p>
              </div>
            </div>
          )}

          {/* Single Device Result */}
          {bindingResult && (
            <Card className="bg-gray-50">
              <CardHeader 
                className="pb-2 cursor-pointer hover:bg-gray-100"
                onClick={() => setBindingResultExpanded(!bindingResultExpanded)}
              >
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {bindingResultExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    {bindingResult.data?.desired ? 'Device Twin' : 'Config Binding Result'}
                  </span>
                  {bindingResult.serial_number && (
                    <Badge variant="outline" className="font-mono">{bindingResult.serial_number}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              {bindingResultExpanded && (
                <CardContent>
                  {bindingResult.data?.desired ? (
                    <DeviceTwinDisplay data={bindingResult.data} />
                  ) : (
                    <pre className="bg-white p-3 rounded border text-xs overflow-x-auto">
                      {JSON.stringify(bindingResult, null, 2)}
                    </pre>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Batch Results */}
          {bindingBatchResults && (
            <Card className="bg-gray-50">
              <CardHeader 
                className="pb-2 cursor-pointer hover:bg-gray-100"
                onClick={() => setBindingBatchResultsExpanded(!bindingBatchResultsExpanded)}
              >
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {bindingBatchResultsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    {bindingBatchResults.isGetTwin ? 'Device Twin Results' : 'Batch Results'}
                  </span>
                  <div className="flex gap-2">
                    <Badge variant="default" className="bg-green-600">
                      {bindingBatchResults.success_count} Success
                    </Badge>
                    {bindingBatchResults.failed_count > 0 && (
                      <Badge variant="destructive">
                        {bindingBatchResults.failed_count} Failed
                      </Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              {bindingBatchResultsExpanded && (
                <CardContent>
                  {bindingBatchResults.isGetTwin ? (
                    // Device Twin results with expandable details
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {bindingBatchResults.results?.map((result, idx) => (
                        <BatchTwinResultItem key={idx} result={result} />
                      ))}
                    </div>
                  ) : (
                    // Regular batch results
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {bindingBatchResults.results?.map((result, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-2 rounded ${
                            result.success ? 'bg-green-50' : 'bg-red-50'
                          }`}
                        >
                          <span className="font-mono text-sm">{result.serial_number}</span>
                          <div className="flex items-center gap-2">
                            {result.success ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}
        </CardContent>
        )}
      </Card>

      {/* Device Configuration Card */}
      <Card className="border-2">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setMdvrConfigExpanded(!mdvrConfigExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6 text-green-600" />
              MDVR Device Configuration
            </div>
            {mdvrConfigExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </CardTitle>
          {mdvrConfigExpanded && (
            <CardDescription>
              Update MDVR device configuration via FTCloud API.
            </CardDescription>
          )}
        </CardHeader>
        {mdvrConfigExpanded && (
        <CardContent className="space-y-4">
          {/* Mode Toggle */}
          <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="batchMode"
              checked={useBatchMode}
              onChange={(e) => setUseBatchMode(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            <Label htmlFor="batchMode" className="flex items-center gap-2 cursor-pointer">
              <Users className="w-4 h-4" />
              Send to multiple devices (batch mode)
            </Label>
          </div>

          {/* Single Device Info */}
          {!useBatchMode && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Using: <span className="font-mono font-medium">{globalDeviceSerial || '(no device)'}</span>
              </span>
            </div>
          )}

          {/* Batch Device Selection */}
          {useBatchMode && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Select Devices ({selectedBatchDevices.length} selected)</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAllBatchDevices}
                  disabled={configuredDevices.length === 0}
                >
                  {selectedBatchDevices.length === configuredDevices.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                {configuredDevices.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No devices configured. Add devices in the Devices tab.
                  </p>
                ) : (
                  configuredDevices.map((device) => (
                    <div
                      key={device.full}
                      className={`flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer ${
                        selectedBatchDevices.includes(device.serial) ? 'bg-green-50' : ''
                      }`}
                      onClick={() => handleBatchDeviceToggle(device.serial)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedBatchDevices.includes(device.serial)}
                        onChange={() => handleBatchDeviceToggle(device.serial)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="font-mono text-sm">{device.serial}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Device Configuration Panel */}
          <DeviceConfigPanel onConfigGenerated={setCurrentConfig} />

          {/* Send Button */}
          <Button
            onClick={handleUpdateConfig}
            disabled={configLoading || (useBatchMode ? selectedBatchDevices.length === 0 : !globalDeviceSerial.trim())}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {configLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {useBatchMode ? `Sending to ${selectedBatchDevices.length} devices...` : 'Updating Configuration...'}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {useBatchMode ? `Send to ${selectedBatchDevices.length} Devices` : 'Update MDVR Configuration'}
              </>
            )}
          </Button>

          {/* Config Error Message */}
          {configError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Error</p>
                <p className="text-sm text-red-600">{configError}</p>
              </div>
            </div>
          )}

          {/* Config Success Message */}
          {configSuccess && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-green-700">Success</p>
                <p className="text-sm text-green-600">{configSuccess}</p>
              </div>
            </div>
          )}

          {/* Config Result Display (Single) */}
          {configResult && !useBatchMode && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-2">Response:</p>
              <pre className="bg-gray-50 p-3 rounded border text-xs overflow-x-auto">
                {JSON.stringify(configResult.data, null, 2)}
              </pre>
            </div>
          )}

          {/* Batch Results Display */}
          {batchResults && useBatchMode && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-4">
                <Badge variant={batchResults.success ? "default" : "secondary"} className="text-sm">
                  Total: {batchResults.total}
                </Badge>
                <Badge variant="default" className="bg-green-100 text-green-700">
                  Success: {batchResults.success_count}
                </Badge>
                {batchResults.failed_count > 0 && (
                  <Badge variant="destructive">
                    Failed: {batchResults.failed_count}
                  </Badge>
                )}
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {batchResults.results.map((result, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-2 rounded border ${
                      result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {result.success ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className="font-mono text-sm">{result.device_id}</span>
                    </div>
                    {!result.success && (
                      <span className="text-xs text-red-600">{result.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Upgrade Files Card */}
      <Card className="border-2">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setUpgradeFilesExpanded(!upgradeFilesExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileUp className="w-6 h-6 text-indigo-600" />
              Upgrade Files
            </div>
            {upgradeFilesExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </CardTitle>
          {upgradeFilesExpanded && (
            <CardDescription>
              Query device upgrade and profile files from FTCloud.
            </CardDescription>
          )}
        </CardHeader>
        {upgradeFilesExpanded && (
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 items-end">
            <div className="flex-1 space-y-1">
              <Label>File Name Filter (optional)</Label>
              <Input
                placeholder="e.g., ADPLUS_V3.5.6.8"
                value={upgradeFileNameFilter}
                onChange={(e) => setUpgradeFileNameFilter(e.target.value)}
              />
            </div>
            <div className="w-40 space-y-1">
              <Label>File Type</Label>
              <select
                value={upgradeFileType}
                onChange={(e) => setUpgradeFileType(e.target.value)}
                className="w-full p-2 border rounded h-10"
              >
                <option value="UPGRADE">Upgrade</option>
                <option value="PROFILE">Profile</option>
              </select>
            </div>
            <Button
              onClick={handleGetUpgradeFiles}
              disabled={upgradeFilesLoading}
            >
              {upgradeFilesLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              <span className="ml-2">Search Files</span>
            </Button>
          </div>

          {/* Error Message */}
          {upgradeFilesError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Error</p>
                <p className="text-sm text-red-600">{upgradeFilesError}</p>
              </div>
            </div>
          )}

          {/* Results */}
          {upgradeFilesResult && upgradeFilesResult.success && (
            <div className="space-y-3">
              {(() => {
                // Deduplicate files by fileId or fileName
                const files = upgradeFilesResult.data?.data || [];
                const uniqueFiles = files.filter((file, index, self) => 
                  index === self.findIndex(f => 
                    (f.fileId && f.fileId === file.fileId) || 
                    (f.fileName && f.fileName === file.fileName)
                  )
                );
                
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-700 flex items-center gap-2">
                        <HardDrive className="w-4 h-4" />
                        Available Files
                      </h4>
                      <Badge variant="outline">
                        {uniqueFiles.length} files found
                      </Badge>
                    </div>

                    {uniqueFiles.length > 0 ? (
                <div className="max-h-96 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="text-left p-3 font-medium w-8"></th>
                        <th className="text-left p-3 font-medium">File Name</th>
                        <th className="text-left p-3 font-medium">Type</th>
                        <th className="text-left p-3 font-medium">Size</th>
                        <th className="text-left p-3 font-medium">Upload Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uniqueFiles.map((file, idx) => (
                        <tr 
                          key={idx} 
                          className={`border-t cursor-pointer transition-colors ${
                            selectedUpgradeFile?.fileId === file.fileId || selectedUpgradeFile?.id === file.id
                              ? 'bg-indigo-50 border-indigo-200'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedUpgradeFile(file)}
                        >
                          <td className="p-3">
                            <input
                              type="radio"
                              checked={selectedUpgradeFile?.fileId === file.fileId || selectedUpgradeFile?.id === file.id}
                              onChange={() => setSelectedUpgradeFile(file)}
                              className="w-4 h-4 text-indigo-600"
                            />
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <File className="w-4 h-4 text-indigo-500" />
                              <span className="font-mono text-xs">{file.fileName || file.name || '-'}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs">
                              {file.fileType || file.type || '-'}
                            </Badge>
                          </td>
                          <td className="p-3 text-gray-600">
                            {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : '-'}
                          </td>
                          <td className="p-3 text-gray-600 text-xs">
                            {file.uploadTime || file.createTime || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <FileUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No files found matching your criteria</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Create Maintenance Task Section */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-3">
              <Upload className="w-4 h-4" />
              Create Maintenance Task
            </h4>
            
            <div className="space-y-3">
              {/* Selected File Display */}
              {selectedUpgradeFile && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <File className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-indigo-700">
                      Selected: {selectedUpgradeFile.fileName || selectedUpgradeFile.name}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedUpgradeFile(null)}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-1">
                  <Label>Device Serial Numbers (comma-separated)</Label>
                  <Input
                    placeholder="e.g., 003B000115, 003B000116"
                    value={upgradeDeviceIds}
                    onChange={(e) => setUpgradeDeviceIds(e.target.value)}
                  />
                </div>
                <div className="w-48 space-y-1">
                  <Label>Task Type</Label>
                  <select
                    value={maintenanceTaskType}
                    onChange={(e) => setMaintenanceTaskType(e.target.value)}
                    className="w-full p-2 border rounded h-10"
                  >
                    <option value="REMOTE_UPGRADE">Remote Upgrade</option>
                    <option value="PROFILE_ISSUED">Profile Issued</option>
                  </select>
                </div>
                <Button
                  onClick={handleCreateMaintenanceTask}
                  disabled={maintenanceTaskLoading || !upgradeDeviceIds.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {maintenanceTaskLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  <span className="ml-2">Create Task</span>
                </Button>
              </div>

              {/* Task Error */}
              {maintenanceTaskError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                  <p className="text-sm text-red-600">{maintenanceTaskError}</p>
                </div>
              )}

              {/* Task Success */}
              {maintenanceTaskResult && maintenanceTaskResult.success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-700">Maintenance task created successfully!</p>
                    {maintenanceTaskResult.data?.requestId && (
                      <p className="text-xs text-green-600 mt-1">
                        Request ID: {maintenanceTaskResult.data.requestId}
                      </p>
                    )}
                    {maintenanceTaskResult.data?.data && maintenanceTaskResult.data.data.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-green-600 font-medium">Task IDs:</p>
                        {maintenanceTaskResult.data.data.map((task, idx) => (
                          <p key={idx} className="text-xs text-green-600 font-mono">
                            {task.maintTaskId || task.taskId || JSON.stringify(task)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Check Task Status Section */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-3">
              <RefreshCw className="w-4 h-4" />
              Check Task Status
            </h4>
            
            <div className="space-y-3">
              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-1">
                  <Label>Task IDs (comma-separated)</Label>
                  <Input
                    placeholder="e.g., 1118339021181812984, 1118387502772650232"
                    value={taskStatusIds}
                    onChange={(e) => setTaskStatusIds(e.target.value)}
                  />
                </div>
                <Button
                  onClick={handleGetTaskStatus}
                  disabled={taskStatusLoading || !taskStatusIds.trim()}
                  variant="outline"
                >
                  {taskStatusLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  <span className="ml-2">Check Status</span>
                </Button>
              </div>

              {/* Status Error */}
              {taskStatusError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                  <p className="text-sm text-red-600">{taskStatusError}</p>
                </div>
              )}

              {/* Status Results */}
              {taskStatusResult && taskStatusResult.success && (
                <div className="space-y-2">
                  {taskStatusResult.data?.data?.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-3 font-medium">Task ID</th>
                            <th className="text-left p-3 font-medium">Device</th>
                            <th className="text-left p-3 font-medium">Status</th>
                            <th className="text-left p-3 font-medium">Progress</th>
                            <th className="text-left p-3 font-medium">Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taskStatusResult.data.data.map((task, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-3 font-mono text-xs">{task.maintTaskId || task.taskId || '-'}</td>
                              <td className="p-3 font-mono text-xs">{task.uniqueId || task.deviceId || '-'}</td>
                              <td className="p-3">
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${
                                    task.status === 'SUCCESS' || task.status === 'COMPLETED' 
                                      ? 'bg-green-100 text-green-700 border-green-300'
                                      : task.status === 'FAILED' || task.status === 'ERROR'
                                      ? 'bg-red-100 text-red-700 border-red-300'
                                      : task.status === 'PENDING' || task.status === 'WAITING'
                                      ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                                      : task.status === 'RUNNING' || task.status === 'IN_PROGRESS'
                                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                                      : 'bg-gray-100 text-gray-700 border-gray-300'
                                  }`}
                                >
                                  {task.status || task.taskStatus || '-'}
                                </Badge>
                              </td>
                              <td className="p-3 text-gray-600">
                                {task.progress !== undefined ? `${task.progress}%` : '-'}
                              </td>
                              <td className="p-3 text-xs text-gray-600 max-w-xs truncate">
                                {task.message || task.errorMsg || task.description || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 border rounded-lg text-center text-gray-500">
                      No task details found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
        )}
      </Card>

      {/* Custom Configuration Card */}
      <Card className="border-2">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setCustomConfigExpanded(!customConfigExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6 text-purple-600" />
              Custom Configuration
            </div>
            {customConfigExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </CardTitle>
          {customConfigExpanded && (
            <CardDescription>
              Send custom configuration parameters to devices.
            </CardDescription>
          )}
        </CardHeader>
        {customConfigExpanded && (
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Custom Parameter *</Label>
              <textarea
                placeholder="Enter custom configuration parameter (refer to documentation)"
                value={customConfigParam}
                onChange={(e) => setCustomConfigParam(e.target.value)}
                className="w-full p-3 border rounded min-h-[120px] font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Enter the custom parameter string as specified in the FTCloud documentation
              </p>
            </div>

            <div className="space-y-1">
              <Label>Device IDs (Serial Numbers) *</Label>
              <Input
                placeholder="e.g., 003B000115, 003B000116"
                value={customConfigDeviceIds}
                onChange={(e) => setCustomConfigDeviceIds(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Comma-separated list of device serial numbers
              </p>
            </div>

            <Button
              onClick={handleCreateCustomConfigTask}
              disabled={customConfigLoading}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {customConfigLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Sending Configuration...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Custom Configuration
                </>
              )}
            </Button>

            {/* Error Message */}
            {customConfigError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                <p className="text-sm text-red-600">{customConfigError}</p>
              </div>
            )}

            {/* Success Message */}
            {customConfigResult && customConfigResult.success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-700">Custom configuration task created successfully!</p>
                  {customConfigResult.data?.requestId && (
                    <p className="text-xs text-green-600 mt-1">
                      Request ID: {customConfigResult.data.requestId}
                    </p>
                  )}
                  {customConfigResult.data?.data && customConfigResult.data.data.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-green-600 font-medium">Task IDs:</p>
                      {customConfigResult.data.data.map((task, idx) => (
                        <p key={idx} className="text-xs text-green-600 font-mono">
                          {task.maintTaskId || task.taskId || JSON.stringify(task)}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
        )}
      </Card>

    </div>
  );
}

export default OnboardingManager;
